import { toMoney } from '@sequencehq/utils'
import {
  Text,
  MagicTable,
  MagicTableCell,
  RowSpanningRule,
  MagicTableFilterConfig,
  useLinkMagicTableWithSearchParams,
  useMultiSelectAsync,
  MagicTableEmptyState,
  MagicTableFilterEmptyState,
  DatePickerInput,
  GreyGrey80,
  Primary13Regular,
  IconReceiptBill,
  IconProjects,
  Icon3dBox,
  Box,
  Flex
} from '@sequencehq/core-components'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import { useState } from 'react'
import { CellContext } from '@tanstack/react-table'
import { format } from 'date-fns'
import {
  DeferredRevenueSchedule,
  useDeferredRevenueDashboardTableQuery
} from './useDeferredRevenueDashboardTableQuery'
import { CurrentUserId } from 'components/CurrentUserId/CurrentUserId'
import { humanize } from 'inflection'
import { DeferredRevenueDetailDrawer } from './DeferredRevenueDetailDrawer'
import { InvoiceLinkCell } from 'RevenueRecognition/view/common/InvoiceLinkCell'
import { ProductLinkCell } from 'RevenueRecognition/view/common/ProductLinkCell'
import { ProgressBar } from 'RevenueRecognition/view/common/ProgressBar'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { customerIdFilterConfig } from 'Customer/index'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

export type DeferredRevenueTableGroupBy =
  | 'customer'
  | 'product'
  | 'invoice'
  | undefined

const columnOrderByGroup: Record<string, string[]> = {
  customer: [
    'customer',
    'product',
    'invoice',
    'method',
    'deferredAmount',
    'recognizedPercentage',
    'recognizedAmount'
  ],
  product: [
    'product',
    'customer',
    'invoice',
    'method',
    'deferredAmount',
    'recognizedPercentage',
    'recognizedAmount'
  ],
  invoice: [
    'invoice',
    'customer',
    'product',
    'method',
    'deferredAmount',
    'recognizedPercentage',
    'recognizedAmount'
  ]
}

type DeferredRevenueFilters = {
  customerId?: string[]
  productId?: string[]
  invoiceId?: string[]
  excludeFullyRecognized?: boolean
}

type DefRevTableView = {
  model: DeferredRevenueSchedule
  controlFilters: DeferredRevenueFilters
  propFilters: Record<string, unknown>
}

const getOrderedColumns = (groupBy: DeferredRevenueTableGroupBy) => {
  const baseColumns = [
    {
      id: 'customer',
      header: 'Customer',
      accessorFn: (row: DeferredRevenueSchedule) => row.customer?.name,
      cell: (value: CellContext<DeferredRevenueSchedule, unknown>) => {
        const customer = value.row.original.customer
        if (!customer) {
          return null
        }

        return (
          <Flex alignItems="center" height="40px" pl="6px" gap={2}>
            <CustomerPreviewCardPill customerId={customer.id} variant="ghost" />
          </Flex>
        )
      }
    },
    {
      id: 'product',
      header: 'Product',
      accessorFn: (row: DeferredRevenueSchedule) => row.product?.name,
      cell: (value: CellContext<DeferredRevenueSchedule, unknown>) => {
        const product = value.row.original.product

        return <ProductLinkCell product={product} />
      }
    },
    {
      id: 'invoice',
      header: 'Invoice',
      accessorFn: (row: DeferredRevenueSchedule) => row.invoice?.name,
      cell: (value: CellContext<DeferredRevenueSchedule, unknown>) => {
        const invoice = value.row.original.invoice

        return <InvoiceLinkCell invoice={invoice} />
      }
    },
    {
      id: 'method',
      header: 'Method',
      accessorFn: (row: DeferredRevenueSchedule) => row.method,
      cell: (value: CellContext<DeferredRevenueSchedule, unknown>) => (
        <MagicTableCell
          text={humanize(value.getValue<string>())}
          data-testid="revrec.defrev.table.method"
        />
      )
    },
    {
      id: 'recognizedAmount',
      header: 'Recognized',
      accessorFn: (row: DeferredRevenueSchedule) =>
        row.progress.recognized.amount,
      cell: (value: CellContext<DeferredRevenueSchedule, unknown>) => (
        <MagicTableCell
          text={toMoney({
            value: `${value.getValue<number>()}`,
            currency: value.row.original.progress.currency
          })}
          textAlign="right"
        />
      ),
      meta: { textAlign: 'right' as const }
    },
    {
      id: 'recognizedPercentage',
      header: 'Progress',
      accessorFn: (row: DeferredRevenueSchedule) =>
        row.progress.recognized.percentage,
      cell: (value: CellContext<DeferredRevenueSchedule, unknown>) => (
        <Flex
          gap={2}
          px={1}
          alignItems="center"
          height="100%"
          justifyContent="end"
          data-testid="revrec.defrev.table.progress"
        >
          <Text {...Primary13Regular} color={GreyGrey80}>
            {value.getValue<number>()}%
          </Text>
          <ProgressBar percentage={value.getValue<number>()} width="76px" />
        </Flex>
      ),
      meta: { textAlign: 'right' as const }
    },
    {
      id: 'deferredAmount',
      header: 'Remaining',
      accessorFn: (row: DeferredRevenueSchedule) =>
        row.progress.remaining.amount,
      cell: (value: CellContext<DeferredRevenueSchedule, unknown>) => (
        <MagicTableCell
          text={toMoney({
            value: `${value.getValue<number>()}`,
            currency: value.row.original.progress.currency
          })}
          textAlign="right"
          data-testid="revrec.defrev.table.remaining"
        />
      ),
      meta: { textAlign: 'right' as const }
    }
  ]

  if (!groupBy) {
    return baseColumns
  }

  const currentOrder = columnOrderByGroup[groupBy]
  return baseColumns.sort((a, b) => {
    const aIndex = currentOrder.indexOf(a.id)
    const bIndex = currentOrder.indexOf(b.id)
    return aIndex - bIndex
  })
}

type InvoiceOption = { id: string; invoiceNumber?: string }

const toInvoiceOptions = (items: InvoiceOption[]) =>
  items
    .filter(
      (invoice): invoice is InvoiceOption & { invoiceNumber: string } =>
        !!invoice.invoiceNumber
    )
    .map(invoice => ({
      value: invoice.id,
      label: invoice.invoiceNumber
    }))

const toProductOptions = (items: { id: string; name: string }[]) =>
  items.map(product => ({
    value: product.id,
    label: product.name
  }))

const useDeferredRevenueDashboardTableFilters = () => {
  const { data: cachedInvoiceOptions } = useQuery(
    dashboardv99990101Client.getInvoices,
    {
      search: '',
      limit: 20
    },
    {
      select: data => toInvoiceOptions(data?.items ?? [])
    }
  )

  const { data: cachedProductOptions } = useQuery(
    dashboardv99990101Client.getProducts,
    {
      name: '',
      limit: 20
    },
    {
      select: data => toProductOptions(data?.items ?? [])
    }
  )

  const [searchParams] = useSearchParams()
  const currentInvoiceIds = searchParams.getAll('invoiceId')
  const currentProductIds = searchParams.getAll('productId')

  const invoiceFilter = useMultiSelectAsync({
    paramName: 'invoiceId' as keyof DeferredRevenueFilters,
    label: 'Invoice',
    icon: IconReceiptBill,
    cachedOptions: cachedInvoiceOptions ?? undefined,
    searchFetcher: async (term: string) => {
      const response = await dashboardv99990101Client.getInvoices({
        search: term,
        limit: 20
      })
      return toInvoiceOptions(response.data?.items ?? [])
    },
    entityFetcher: async (id: string) => {
      const response = await dashboardv99990101Client.getInvoice({ id })
      return {
        value: response.data?.id || id,
        label: response.data?.invoiceNumber || response.data?.id || id
      }
    },
    activeFilterValues: currentInvoiceIds
  })

  const productFilter = useMultiSelectAsync({
    paramName: 'productId' as keyof DeferredRevenueFilters,
    label: 'Product',
    icon: Icon3dBox,
    cachedOptions: cachedProductOptions ?? undefined,
    searchFetcher: async (term: string) => {
      const response = await dashboardv99990101Client.getProducts({
        name: term,
        limit: 20
      })
      return toProductOptions(response.data?.items ?? [])
    },
    entityFetcher: async (id: string) => {
      const response = await dashboardv99990101Client.getProduct({ id })
      return {
        value: response.data?.id || id,
        label: response.data?.name || id
      }
    },
    activeFilterValues: currentProductIds
  })

  const filtersConfig: MagicTableFilterConfig<DeferredRevenueFilters>[] = [
    {
      ...customerIdFilterConfig,
      paramName: 'customerId'
    },
    invoiceFilter as MagicTableFilterConfig<DeferredRevenueFilters>,
    productFilter as MagicTableFilterConfig<DeferredRevenueFilters>,
    {
      type: 'toggle',
      paramName: 'excludeFullyRecognized',
      label: 'Hide completed obligations'
    }
  ]

  const { activeFilters, onChangeActiveFilters } =
    useLinkMagicTableWithSearchParams(filtersConfig)

  return {
    activeFilters,
    onChangeActiveFilters,
    filtersConfig
  }
}

type Props = {
  asOfDate: Date
  setAsOfDate: (date: Date) => void
  groupBy: DeferredRevenueTableGroupBy
  setGroupBy: (groupBy: DeferredRevenueTableGroupBy) => void
}

export const DeferredRevenueDashboardTable = ({
  asOfDate,
  setAsOfDate,
  groupBy,
  setGroupBy
}: Props) => {
  const navigate = useNavigate()

  const [selectedRow, setSelectedRow] =
    useState<DeferredRevenueSchedule | null>(null)

  const { activeFilters, onChangeActiveFilters, filtersConfig } =
    useDeferredRevenueDashboardTableFilters()

  const { infiniteQuery } = useDeferredRevenueDashboardTableQuery({
    groupBy,
    filtersConfig,
    activeFilters,
    asOfDate: format(asOfDate, 'yyyy-MM-dd')
  })

  const columns = getOrderedColumns(groupBy)

  return (
    <Box height="100%" data-testid="revrec.defrev.table">
      {selectedRow && (
        <DeferredRevenueDetailDrawer
          selectedScheduleId={selectedRow.id}
          canRecognizeRevenue={selectedRow.method === 'MILESTONE'}
          closeDrawer={() => setSelectedRow(null)}
        />
      )}
      <CurrentUserId>
        {userId => (
          <MagicTable<DefRevTableView>
            key={groupBy}
            entityNamePlural="deferred revenue schedules"
            enableColumnReordering={false}
            infiniteQuery={infiniteQuery}
            columns={columns}
            sequenceUserId={userId}
            filters={filtersConfig}
            activeFilters={activeFilters}
            onChangeActiveFilters={onChangeActiveFilters}
            onRowClick={row => {
              setSelectedRow(row)
            }}
            additionalToolbarComponentsV3={{
              start: (
                <Flex alignItems="center" gap="8px">
                  <Text
                    {...Primary13Regular}
                    color={GreyGrey80}
                    whiteSpace="nowrap"
                  >
                    Show deferred revenue as of
                  </Text>
                  <DatePickerInput
                    value={asOfDate}
                    onChange={newDate => setAsOfDate(newDate ?? new Date())}
                    dateFormat="d MMM yyyy"
                    width="100px"
                    hideCalendarIcon
                    data-testid="revrec.defrev.asOfDate"
                  />
                </Flex>
              )
            }}
            emptyStateUnfiltered={entityNamePlural => (
              <MagicTableEmptyState
                entityNamePlural={entityNamePlural}
                imageSrc="/img/empty/invoices.png"
                learnMoreText="Learn more about deferred revenue schedules"
                learnMoreHref="https://docs.sequencehq.com/revenue-recognition"
              />
            )}
            emptyStateFiltered={(entityNamePlural, clearFilters) => (
              <MagicTableFilterEmptyState
                entityNamePlural={entityNamePlural}
                entityIcon={IconProjects}
                actionClearFilters={clearFilters}
                actionViewAll={() =>
                  navigate('/revenue-recognition/deferred-revenue')
                }
                variant="TABLE"
              />
            )}
            grouping={{
              rowSpanningRules: [
                {
                  columnId: 'invoice',
                  spanBy: (current, previous) => {
                    if (groupBy === 'invoice') {
                      return current.invoice?.id === previous.invoice?.id
                    }

                    return false
                  }
                } as RowSpanningRule<DeferredRevenueSchedule>,
                {
                  columnId: 'customer',
                  spanBy: (current, previous) => {
                    if (groupBy === 'customer') {
                      return current.customer?.id === previous.customer?.id
                    }

                    return false
                  }
                } as RowSpanningRule<DeferredRevenueSchedule>,
                {
                  columnId: 'product',
                  spanBy: (current, previous) => {
                    if (groupBy === 'product') {
                      return current.product?.id === previous.product?.id
                    }

                    return false
                  }
                } as RowSpanningRule<DeferredRevenueSchedule>,
                {
                  columnId: 'method',
                  spanBy: (current, previous) => {
                    return current.id === previous.id
                  }
                },
                {
                  columnId: 'recognizedAmount',
                  spanBy: (current, previous) => {
                    return current.id === previous.id
                  }
                },
                {
                  columnId: 'deferredAmount',
                  spanBy: (current, previous) => {
                    return current.id === previous.id
                  }
                },
                {
                  columnId: 'recognizedPercentage',
                  spanBy: (current, previous) => {
                    return current.id === previous.id
                  }
                }
              ],
              groupBy,
              onGroupBy: (columnId?: string) => {
                setGroupBy(columnId as DeferredRevenueTableGroupBy)
              },
              groupableColumns: ['invoice', 'customer', 'product'],
              highlightRowGroup: row => row.id,
              getGroupKey: row => {
                switch (groupBy) {
                  case 'invoice':
                    return row.invoice?.id ?? row.id
                  case 'customer':
                    return row.customer?.id ?? row.id
                  case 'product':
                    return row.product?.id ?? row.id
                  default:
                    return row.id
                }
              }
            }}
          />
        )}
      </CurrentUserId>
    </Box>
  )
}
